import { SearchspringCollectionProvider } from 'libs/searchspring/fetchers/collection'

import clsx from 'clsx'
import { useEffect, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useInfiniteScrollResults } from 'libs/searchspring/hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from 'libs/searchspring/components/collection/InfiniteScroll'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { CustomFilter } from 'libs/searchspring/components/collection/filters/CustomFilter'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/searchspring/components/collection/filters/FilterDrawer'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'

function CollectionGrid({ showFilters, customFilter }) {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const { allResults, gridChange, isLoading } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && allResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, allResults?.length, firstLoad])

  useScrollCache([allResults, firstLoad])

  return (
    <div className="grow flex flex-col">
      <FiltersProvider>
        {customFilter && <CustomFilter filter={customFilter} />}
        <CollectionToolbar />
        <div className="grow flex flex-col md:grid md:grid-cols-min-full md:px-margin">
          <FilterDrawer showFilters={showFilters} />
          <div className="grow flex flex-col">
            <div
              ref={(el) => { setScrollElement(el); setEntry(el) }}
              className={clsx('grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 xxxl:grid-cols-5 [grid-auto-flow:row_dense] gap-x-gutter gap-y-2 w-full lg:pt-2', (isLoading && firstLoad) && 'min-h-screen-height')}
            >
              <InfiniteScrollPrevious />
              {showSkeleton && firstLoad ? (
                Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                ))
              ) : (
                allResults?.map((item, index) => {
                  return <ResultItem item={item} key={item.handle} className="wk-wishlist-button-target" dataIndex={index.toString()} />
                })
              )}
            </div>
            {allResults.length > 0 && !firstLoad && (
              <div className="flex justify-center px-margin py-3.5">
                <InfiniteScrollNext />
              </div>
            )}
          </div>
        </div>
      </FiltersProvider>
    </div>
  )
}

export function WrappedCollectionGrid({ customFilter, perPage, 'show-filters': showFilters }) {
  const resultsPerPage = perPage || window.settings?.per_page
  scrollHistoryManager.updateHistoryState()
  return (
    <SearchspringCollectionProvider handle={window.collection?.handle} resultsPerPage={resultsPerPage}>
      <CollectionGrid customFilter={customFilter} showFilters={showFilters === 'true'} />
    </SearchspringCollectionProvider>
  )
}
